import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  registerCustomer as apiRegister,
  loginCustomer as apiLogin,
  verifyEmail as apiVerifyEmail,
  forgotPassword as apiForgotPassword,
  resetPassword as apiResetPassword,
  fetchMe,
  getStoredUser,
  setSession,
  clearSession,
} from '../lib/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Real auth state — backed by /wp-json/lalay/v1/* via src/lib/auth.js.
 * The session lives in localStorage under lalay:token + lalay:user.
 * Stored-role compatibility: WP returns role 'customer' for shoppers;
 * existing role-checking callers expect 'user' for shopper screens, so we
 * normalize on hydrate.
 */
function normalizeUser(raw) {
  if (!raw) return null;
  return {
    ...raw,
    name: raw.displayName || raw.username || raw.email,
    role: raw.role || 'user',
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Hydrate session on mount: read cached user, then verify token via /me
  useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      try {
        const cached = normalizeUser(getStoredUser());
        if (cached) setUser(cached);
        const fresh = await fetchMe(); // null if no token or token invalid
        if (cancelled) return;
        setUser(fresh ? normalizeUser(fresh) : null);
      } catch (e) {
        console.error('[auth] hydrate failed:', e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    hydrate();
    return () => { cancelled = true; };
  }, []);

  const login = async (credentials) => {
    setIsLoading(true);
    try {
      const res = await apiLogin({
        email: credentials?.email,
        username: credentials?.username,
        password: credentials?.password,
      });
      if (!res.success) return { success: false, error: res.message };
      const normalized = normalizeUser(res.user);
      setUser(normalized);
      const params = new URLSearchParams(window.location.search);
      const redirect = params.get('redirect');
      navigate(redirect && redirect.startsWith('/') ? redirect : '/user-account-dashboard');
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error?.message || 'Login failed' };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data) => {
    setIsLoading(true);
    try {
      const res = await apiRegister({
        email: data?.email,
        password: data?.password,
        firstName: data?.firstName,
        lastName: data?.lastName,
        username: data?.username,
      });
      if (!res.success) return { success: false, error: res.message, code: res.code };
      const normalized = normalizeUser(res.user);
      setUser(normalized);
      const params = new URLSearchParams(window.location.search);
      const redirect = params.get('redirect');
      navigate(redirect && redirect.startsWith('/') ? redirect : '/user-account-dashboard');
      return { success: true, user: normalized };
    } catch (error) {
      console.error('Register error:', error);
      return { success: false, error: error?.message || 'Registration failed' };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyEmail = async (token) => {
    const res = await apiVerifyEmail(token);
    if (res.success && res.user) setUser(normalizeUser(res.user));
    return res;
  };

  const forgotPassword = async (email) => apiForgotPassword(email);
  const resetPassword  = async (payload) => {
    const res = await apiResetPassword(payload);
    if (res.success && res.user) setUser(normalizeUser(res.user));
    return res;
  };

  const logout = () => {
    clearSession();
    setUser(null);
    navigate('/user-login');
  };

  const isAuthenticated = () => user !== null;
  const hasRole = (role) => {
    if (!user) return false;
    if (user?.activeView === 'user' && role === 'user') return true;
    return user?.role === role;
  };

  // Role-view switching kept for compatibility with existing admin flows
  const switchToUserView = () => {
    if (user?.role === 'admin') {
      const next = { ...user, activeView: 'user', originalRole: user.role };
      setSession(undefined, next);
      setUser(next);
      navigate('/user-account-dashboard');
    }
  };
  const switchToAdminView = () => {
    if (user?.originalRole === 'admin' || user?.role === 'admin') {
      const next = { ...user, activeView: 'admin', role: user?.originalRole || user.role };
      delete next.activeView;
      setSession(undefined, next);
      setUser(next);
      navigate('/admin-dashboard');
    }
  };
  const isInUserView = () => user?.activeView === 'user';
  const canSwitchToAdminView = () => user?.role === 'admin' || user?.originalRole === 'admin';

  const value = {
    user,
    isLoading,
    login,
    register,
    verifyEmail,
    forgotPassword,
    resetPassword,
    logout,
    isAuthenticated,
    hasRole,
    switchToUserView,
    switchToAdminView,
    isInUserView,
    canSwitchToAdminView,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
